import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { LoaderComponent } from '@examdojo/ui/loader';
import { EMPTY, finalize, tap } from 'rxjs';
import { PayoutRatesHttpService } from '../payout-rates/payout-rates-http.service';
import { PayoutRateHttpModel } from '../payout-rates/payout-rates.model';

@Component({
  selector: 'dojo-payout-rate-history',
  standalone: true,
  imports: [IconComponent, LoaderComponent, DecimalPipe],
  template: `
    <div class="flex h-full flex-col">
      @if (!hideHeader()) {
        <div class="mb-4 flex items-center gap-3">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-lg border border-sky-100 bg-sky-50 dark:border-sky-800 dark:bg-sky-900/20"
          >
            <dojo-icon [icon]="'clock-rotate-left'" [size]="20" class="text-sky-600 dark:text-sky-400" />
          </div>
          <div>
            <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">Hourly Rate History</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">View all historical rate changes</p>
          </div>
        </div>
      }

      @if (isLoading()) {
        <div class="flex items-center justify-center py-16">
          <dojo-loader />
        </div>
      } @else if (error()) {
        <div class="rounded-xl border border-red-200 bg-red-50 p-6 dark:border-red-800 dark:bg-red-900/20">
          <div class="flex items-start gap-4">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <dojo-icon [icon]="'exclamation-triangle'" [size]="20" class="text-red-600 dark:text-red-400" />
            </div>
            <div class="flex-1">
              <h3 class="font-medium text-red-900 dark:text-red-200">Error loading rate history</h3>
              <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                Unable to fetch the rate history. Please try again.
              </p>
            </div>
          </div>
        </div>
      } @else if (history() !== null && history()!.length === 0) {
        <div class="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800">
          <div class="flex items-start gap-4">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
              <dojo-icon [icon]="'info-circle'" [size]="20" class="text-gray-600 dark:text-gray-400" />
            </div>
            <div class="flex-1">
              <h3 class="font-medium text-gray-900 dark:text-gray-100">No rate history</h3>
              <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                No historical rate changes found for this user.
              </p>
            </div>
          </div>
        </div>
      } @else if (history() !== null) {
        <div
          class="flex-1 overflow-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
        >
          <table class="w-full">
            <thead class="sticky top-0 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
              <tr>
                <th
                  class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300"
                >
                  Rate
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300"
                >
                  Valid From
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300"
                >
                  Valid To
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              @for (entry of history(); track $index) {
                <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td class="px-6 py-4">
                    <span
                      class="inline-flex items-center rounded-full bg-sky-100 px-3 py-1 text-sm font-semibold text-sky-800 dark:bg-sky-900/30 dark:text-sky-200"
                    >
                      <dojo-icon [icon]="'euro-sign'" class="mr-1.5" [size]="14" />
                      {{ entry.rate | number: '1.2-2' }}/hour
                    </span>
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                    @if (entry.valid_from) {
                      {{ formatDate(entry.valid_from) }}
                    } @else {
                      <span class="italic text-gray-500 dark:text-gray-400">Beginning of time</span>
                    }
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    @if (entry.valid_to) {
                      {{ formatDate(entry.valid_to) }}
                    } @else if (entry.id === currentRateId()) {
                      <span class="font-medium text-green-600 dark:text-green-400">Current</span>
                    } @else {
                      <span class="text-gray-400 dark:text-gray-500">—</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full' },
})
export class PayoutRateHistoryComponent {
  constructor() {
    effect(
      () => {
        const userId = this.userId();
        if (userId) {
          this.loadHistory(userId);
        }
      },
      { allowSignalWrites: true },
    );
  }

  private readonly httpService = inject(PayoutRatesHttpService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly dateTimeService = inject(DateTimeService);
  private readonly destroyRef = inject(DestroyRef);

  readonly userId = input.required<string>();
  readonly hideHeader = input<boolean>(false);

  protected readonly isLoading = signal<boolean>(false);
  protected readonly error = signal<string | null>(null);
  protected readonly history = signal<PayoutRateHttpModel[] | null>(null);
  protected readonly currentRateId = signal<string | null>(null);

  formatDate(dateString: string): string {
    return this.dateTimeService.formatDate(new Date(dateString), DateTimeFormat.ShortDateTime);
  }

  private findCurrentRateId(rates: PayoutRateHttpModel[]): string | null {
    const now = new Date();

    const currentRate = rates.find((rate) => {
      // If valid_from is null, treat as beginning of time
      const validFrom = rate.valid_from ? new Date(rate.valid_from) : new Date(0);

      // If valid_to exists, check if now is in the range
      if (rate.valid_to) {
        const validTo = new Date(rate.valid_to);
        return now >= validFrom && now < validTo;
      }

      // If valid_to is null, it's current only if valid_from is in the past or now
      return now >= validFrom;
    });

    return currentRate?.id ?? null;
  }

  private loadHistory(userId: string): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.httpService
      .fetchUserRateHistory(userId)
      .pipe(
        tap((history) => {
          this.history.set(history);
          // Calculate which rate is current once when data loads
          this.currentRateId.set(this.findCurrentRateId(history));
        }),
        this.errorHandlerService.catchHttpErrors(() => {
          this.error.set('Failed to load rate history');
          this.history.set([]);
          this.currentRateId.set(null);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
